import { AllProjectsPage, NewProjectPage, ProjectPage, LoginPage, SignUpPage, VerifyEmailPage, GetStartedPage, RequestPasswordResetPage, ResetPasswordPage, WelcomePage, AdminPage, SuperAdminPage, TicketsPage, BillingPage, MyTasksPage, ViewDesign, Terms, Privacy, CashFlowForecastPage, TutorialsPage } from './components/pages';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import { CartProvider } from './context/CartContext';
import { useEffect, useRef, useCallback, useState } from 'react';
import { fetchData, initGA, trackPageView, initHubSpot, disableHubSpot, trackHubSpotPageView, identifyHubSpotVisitor, GA_MEASUREMENT_ID, syncAnalyticsConsentState, setAnalyticsUserId, clearQueuedAnalyticsEvents, consumePendingCompanyCreated, consumePendingUserCreated, getUserRole, trackInvoicePaidTransitions } from './services';
import { getProjectsAllUrl } from './utils/projects';
import { hasAnalyticsConsent, COOKIE_CONSENT_EVENT, COOKIE_CONSENT } from './utils/cookieConsent';
import Navbar from './components/Navbar';
import Alert from './components/helpers/Alert';
import AutoDesignerModal from './components/helpers/AutoDesignerModal';
import CookieBanner from './components/helpers/CookieBanner';
import ErrorBoundary from './components/helpers/ErrorBoundary';
import ThemeToggle from './components/helpers/ThemeToggle';
import PageNotFound from './components/blocks/PageNotFound';
import { canAccessCashForecast } from './utils/cashForecastAccess';


// Wrapper component to handle route parameters
function ProjectPageWrapper() {
  const { projectId } = useParams();
  const { projects, token, isLoadingProjects } = useAppContext();

  if (!token) {
    return <div>Loading...</div>;
  }

  if (isLoadingProjects) {
    return <div>Loading projects...</div>;
  }

  const project = projects?.find(p => p.id === Number(projectId));

  if (!project) {
    return <div>Project not found</div>;
  }

  return <ProjectPage project={project} />;
}

function CashFlowForecastPageWrapper() {
  const { user, isLoadingCompany } = useAppContext();

  if (isLoadingCompany) {
    return <div>Loading...</div>;
  }

  if (!canAccessCashForecast(user)) {
    return <Navigate to="/" replace />;
  }

  return <CashFlowForecastPage />;
}

function SettingsRedirect() {
  const { user } = useAppContext();
  if (user?.is_super_admin) {
    return <Navigate to="/super-admin?section=profile" replace />;
  }
  return <Navigate to="/admin?tab=profile" replace />;
}

// Component to handle location-based project loading inside Router context
function LocationBasedProjectLoader() {
  const { token, base_url, setProjects, setIsLoadingProjects } = useAppContext();
  const hasLoadedRef = useRef(false);

  const loadProjects = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoadingProjects(true);
      const result = await fetchData(token, getProjectsAllUrl(base_url));

      if (result?.projects) {
        setProjects(result.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [token, base_url, setProjects, setIsLoadingProjects]);

  useEffect(() => {
    if (!token) {
      hasLoadedRef.current = false;
      return;
    }

    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadProjects();
    }
  }, [token, loadProjects]);

  return null;
}

function InvoicesLoader() {
  const { token, base_url, setInvoices, setIsLoadingInvoices } = useAppContext();

  const loadInvoices = useCallback(async ({ showLoading = false } = {}) => {
    if (!token) return;

    try {
      if (showLoading) {
        setIsLoadingInvoices(true);
      }
      const result = await fetchData(token, `${base_url}/apis/billing/invoices`);

      if (result?.invoices) {
        setInvoices(result.invoices);
      }
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      if (showLoading) {
        setIsLoadingInvoices(false);
      }
    }
  }, [token, base_url, setInvoices, setIsLoadingInvoices]);

  useEffect(() => {
    if (!token) {
      return;
    }

    loadInvoices({ showLoading: true });
    const invoicesInterval = setInterval(() => loadInvoices({ showLoading: false }), 60_000);
    return () => clearInterval(invoicesInterval);
  }, [token, loadInvoices]);

  return null;
}

function QuotesLoader() {
  const { token, base_url, user, setQuotes, setIsLoadingQuotes } = useAppContext();
  const hasLoadedRef = useRef(false);
  const isBillingUser = Boolean(user?.is_admin || user?.is_super_admin);

  const loadQuotes = useCallback(async () => {
    if (!token || !isBillingUser) return;

    try {
      setIsLoadingQuotes(true);
      const result = await fetchData(token, `${base_url}/apis/billing`);

      if (result?.quotes) {
        setQuotes(result.quotes);
      }
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setIsLoadingQuotes(false);
    }
  }, [token, base_url, isBillingUser, setQuotes, setIsLoadingQuotes]);

  useEffect(() => {
    if (!token || !isBillingUser) {
      hasLoadedRef.current = false;
      setQuotes([]);
      setIsLoadingQuotes(false);
      return;
    }

    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadQuotes();
    }
  }, [token, isBillingUser, loadQuotes, setQuotes, setIsLoadingQuotes]);

  return null;
}

function InvoicePaidTracker() {
  const { invoices, user, company, token } = useAppContext();
  const previousInvoicesRef = useRef(null);

  useEffect(() => {
    if (!token) {
      previousInvoicesRef.current = null;
      return;
    }

    trackInvoicePaidTransitions(previousInvoicesRef.current, invoices, {
      companyId: company?.id || user?.company_id,
    });
    previousInvoicesRef.current = invoices;
  }, [token, invoices, company?.id, user?.company_id]);

  return null;
}

// Component to handle Google Analytics and HubSpot page tracking
function AnalyticsTracker() {
  const location = useLocation();
  const { user, company } = useAppContext();
  const [gaReady, setGaReady] = useState(false);
  const [hubSpotReady, setHubSpotReady] = useState(false);
  const isFirstHubSpotPage = useRef(true);
  const identifiedEmail = useRef(null);

  const tryInitializeGA = useCallback(async () => {
    if (!hasAnalyticsConsent()) {
      return false;
    }

    if (!GA_MEASUREMENT_ID) {
      console.warn('GA4 Measurement ID not found.');
      return false;
    }

    const initialized = await initGA(GA_MEASUREMENT_ID);
    if (initialized) {
      setGaReady(true);
    }

    return initialized;
  }, []);

  const tryInitializeHubSpot = useCallback(() => {
    if (!hasAnalyticsConsent()) {
      return false;
    }

    const initialized = initHubSpot();
    if (initialized) {
      setHubSpotReady(true);
    }

    return initialized;
  }, []);

  useEffect(() => {
    tryInitializeGA();
    tryInitializeHubSpot();
  }, [tryInitializeGA, tryInitializeHubSpot]);

  useEffect(() => {
    const handleConsentChange = async (event) => {
      if (event.detail === COOKIE_CONSENT.ACCEPTED) {
        await tryInitializeGA();
        tryInitializeHubSpot();
      } else if (event.detail === COOKIE_CONSENT.DECLINED) {
        syncAnalyticsConsentState();
        clearQueuedAnalyticsEvents();
        disableHubSpot();
      }
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
  }, [tryInitializeGA, tryInitializeHubSpot]);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (gaReady) {
      trackPageView(path);
    }
    if (hubSpotReady) {
      trackHubSpotPageView(path, { isInitial: isFirstHubSpotPage.current });
      isFirstHubSpotPage.current = false;
    }
  }, [gaReady, hubSpotReady, location]);

  useEffect(() => {
    if (!hubSpotReady || !user?.email || identifiedEmail.current === user.email) {
      return;
    }

    identifyHubSpotVisitor({
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
    });
    identifiedEmail.current = user.email;

    // Identity is only sent with a page view. The tracking script already
    // recorded the first load, so send this one so the contact is linked.
    if (!isFirstHubSpotPage.current) {
      trackHubSpotPageView(location.pathname + location.search);
    }
  }, [hubSpotReady, user?.email, user?.first_name, user?.last_name, location.pathname, location.search]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setAnalyticsUserId(user.id);
    consumePendingCompanyCreated({
      companyId: company?.id || user.company_id,
      userId: user.id,
    });
    consumePendingUserCreated({
      companyId: company?.id || user.company_id,
      userId: user.id,
      userRole: getUserRole(user),
    });
  }, [user, company?.id]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function PostSignupRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAppContext();

  useEffect(() => {
    if (token && sessionStorage.getItem('newCompanySignup') === '1' && location.pathname !== '/new-project') {
      navigate('/new-project', { replace: true });
    }
  }, [token, navigate, location.pathname]);

  return null;
}

function AppContent() {
  const { token, base_url, setUser, setCompany, setIsLoadingCompany } = useAppContext();

  // Load user and company when there is a token available
  useEffect(() => {
    const loadUserAndCompany = async () => {
      if(token) {
        try {
          const profile = await fetchData(token, `${base_url}/apis/admin/profile`);

          if (profile.user) {
            setUser(profile.user);
          }

          if (profile.company) {
            setCompany(profile.company);
          }
        } catch (error) {
          console.error('Error loading user and company:', error);
        } finally {
          setIsLoadingCompany(false);
        }
      }
    }

    loadUserAndCompany();
  }, [token, base_url, setUser, setCompany, setIsLoadingCompany]);

  return (
    <Router>
      <LocationBasedProjectLoader />
      <InvoicesLoader />
      <QuotesLoader />
      <InvoicePaidTracker />
      <AnalyticsTracker />
      <ScrollToTop />
      <PostSignupRedirect />
      <CookieBanner />
      <div className="app-container">
        {token ? (
          <>
            <Navbar />
            <div className="content-container">
              <Routes>
                <Route path="/" element={<AllProjectsPage />} />
                <Route path="/get-started" element={<GetStartedPage />} />
                <Route path="/completed-projects" element={<Navigate to="/" replace />} />
                <Route path="/new-project" element={<NewProjectPage />} />
                <Route path="/project/:projectId" element={<ProjectPageWrapper />} />
                <Route path="/view-design/:designId" element={<ViewDesign />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/super-admin" element={<SuperAdminPage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/admin/email-logs" element={<Navigate to="/super-admin" replace />} />
                <Route path="/company" element={<Navigate to="/admin" replace />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/invoices" element={<Navigate to="/billing?tab=invoices" replace />} />
                <Route path="/manage-users" element={<Navigate to="/admin?tab=users" replace />} />
                <Route path="/tasks" element={<MyTasksPage />} />
                <Route path="/my-tasks" element={<Navigate to="/tasks" replace />} />
                <Route path="/cash-forecast" element={<CashFlowForecastPageWrapper />} />
                <Route path="/settings" element={<SettingsRedirect />} />
                <Route path="/tutorials" element={<TutorialsPage />} />
                {/* Store UI kept in codebase; routes disabled until a later release */}
                <Route path="/store" element={<PageNotFound />} />
                <Route path="/store/*" element={<PageNotFound />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/welcome/:user_id" element={<WelcomePage />} />
                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </div>
            <Alert />
            <AutoDesignerModal />
          </>
        ) : (
          <div className="guest-container">
            <ThemeToggle variant="floating" />
            <Routes>
              {/* Store UI kept in codebase; routes disabled until a later release */}
              <Route path="/store" element={<PageNotFound />} />
              <Route path="/store/*" element={<PageNotFound />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/tutorials" element={<TutorialsPage />} />
              <Route path="/view-design/:designId" element={<ViewDesign />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/request-password-reset" element={<RequestPasswordResetPage />} />
              <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
              <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
              <Route path="/welcome/:user_id" element={<WelcomePage />} />
              <Route path="*" element={<LoginPage />} />
            </Routes>
            <Alert />
          </div>
        )}
      </div>
    </Router>
  )
}

// Wrap the AppContent in the AppProvider and ErrorBoundary
function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
